/* global google */
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  FieldConfiguration,
  ProviderData,
  SearchProviderSelectedFilterType,
} from "./SearchProvider.types";

import GooglePlacesAutocomplete, {
  geocodeByLatLng,
  geocodeByPlaceId,
} from "react-google-places-autocomplete";
import GooglePlacesAutocompleteProps, {
  GooglePlacesAutocompleteHandle,
  Option,
} from "react-google-places-autocomplete/build/types";
import SearchProviderFilterModal from "./SearchProviderFilterModal";
import {
  SearchProviderContext,
  doProviderFilter,
} from "./SearchProviderContext";
import MemoCloseIcon from "../Components/CloseIcon";
import {
  autocompleteOptions,
  autocompleteStyles,
  autocompleteTheme,
} from "./AutocompleteStyles";

type SearchProviderFormProps = Pick<ProviderData, "configuration">;

export function filterNameFromString(name: string): string {
  return name
    .replace(/[^a-zA-Z]/g, "-")
    .replace(/-+/g, "-")
    .trim()
    .toLowerCase();
}

const SearchProviderForm: React.FC<SearchProviderFormProps> = ({
  configuration,
}) => {
  const { fields, apiKey } = configuration;
  const formRef = useRef<HTMLFormElement>(null);
  const selectInputRef = useRef<GooglePlacesAutocompleteHandle>(null);
  const [isUserLocation, setIsUserLocation] = useState(false);
  const [isApiLoaded, setIsApiLoaded] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [renderAutocomplete, setRenderAutocomplete] = useState(true);
  const [locationLoading, setLocationLoading] = useState(false);
  const [showLocationError, setShowLocationError] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState<Option | null>(null);
  const providerContext = useContext(SearchProviderContext);
  const [selectedFilters, setSelectedFilters] = useState<
    SearchProviderSelectedFilterType[]
  >([]);

  // Get the location filter
  const locationFilter: FieldConfiguration = fields
    ? fields.find((filter) => filter.type === "address" && filter.showInSearch)
    : undefined;

  // Get the the radio filters
  const radioFilters: FieldConfiguration[] = fields
    ? fields.filter(
        (filter) =>
          (filter.type === "radio" ||
            filter.type.toLowerCase() === "commaseparatedlist") &&
          filter.showInSearch
      )
    : undefined;

  const modalFilters = fields.filter((filter) => {
    return filter.showInModal;
  });

  const activeModalFilters = selectedFilters.filter((filter) => {
    return modalFilters.find((f) => f.name === filter.name);
  });

  const filterCount = activeModalFilters.length;

  const {
    setProviders,
    providers,
    setLoading,
    showResults,
    setShowResults,
    manualPlace,
    setNearbyRegions,
  } = providerContext;

  // check if google maps api is loaded
  useEffect(() => {
    const checkGoogle = setInterval(() => {
      if (
        typeof window.google === "object" &&
        typeof window.google.maps === "object"
      ) {
        setIsApiLoaded(true);
        clearInterval(checkGoogle);
      }
    }, 200);
  }, []);

  useEffect(() => {
    if (!selectedPlace) setRenderAutocomplete(false);
  }, [selectedPlace]);

  useEffect(() => {
    if (manualPlace) {
      setSelectedPlace(manualPlace);
      setNearbyRegions([]);
    }
  }, [manualPlace]);

  useEffect(() => {
    if (!renderAutocomplete) setRenderAutocomplete(true);
  }, [renderAutocomplete]);

  /**
   * Handles updating the filters and saving the state to the URL.
   * @param filters
   */
  const updateFilters = (filters: SearchProviderSelectedFilterType[]) => {
    setSelectedFilters(filters);

    // update the window.location with the new filters
    const searchParams = new URLSearchParams();
    filters.forEach((filter) => {
      searchParams.set(filter.name, filter.value);
    });

    if (selectedPlace) {
      searchParams.set("placeId", selectedPlace.value.place_id);
    }

    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${searchParams.toString()}`
    );
  };

  const location = window.location;

  useEffect(() => {
    // decode the search params and update the filters
    const searchParams = new URLSearchParams(location.search);

    const filters: SearchProviderSelectedFilterType[] = [];

    searchParams.forEach((value, key) => {
      const lcKey = key.toLowerCase();

      // ignore the stage param
      if (lcKey == "stage") {
        return;
      }

      // ignore the flush param
      if (lcKey == "flush") {
        return;
      }

      if (key === "placeId" && isApiLoaded) {
        // get the place details from the placeId
        try {
          geocodeByPlaceId(value).then((results) => {
            if (results && results.length > 0) {
              const firstResult = results[0];

              setSelectedPlace({
                label: firstResult.formatted_address,
                value: firstResult,
              } as Option);
            }
          });
        } catch (error) {
          console.error("Error trying to geocode by placeId", value, error);
        }
      } else {
        filters.push({
          name: key,
          value: value,
        });

        // trigger the filter change event
        const input = formRef.current?.querySelector(
          `input[name="${key}"]`
        ) as HTMLInputElement;

        if (input) {
          input.checked = true;
        }
      }
    });

    setSelectedFilters(filters);
  }, [location, isApiLoaded]);

  /**
   * Reset the form and clear the selected place.
   * @param {React.MouseEvent<HTMLButtonElement>} e - the click event
   * @return {void}
   */
  const resetForm = (e: React.MouseEvent<HTMLButtonElement>) => {
    setLoading(true);
    setShowResults(false);
    setIsUserLocation(false);
    setLocationLoading(false);

    e.preventDefault();

    if (formRef.current) {
      formRef.current.reset();
      setSelectedPlace(null);
    }

    updateFilters([]);
    setShowLocationError(false);
    updateProviders([], null);
  };

  /**
   * A function to handle form submission.
   */
  const updateProviders = (filters, place: Option | null) => {
    setLoading(true);

    let coords: { lat: number; lng: number; range: number } | null = null;

    if (place && locationFilter) {
      // range is based on the place type
      let range = 10;

      if (place.value.types.includes("administrative_area_level_1")) {
        range = 50;
      }

      if (place.value.types.includes("locality")) {
        range = 30;
      }

      coords = {
        lat: place.value.geometry.location.lat(),
        lng: place.value.geometry.location.lng(),
        range: range,
      };
    }

    const results = doProviderFilter(filters, providers, coords);

    setProviders(results);
    setLoading(false);

    // update the nearby regions
    if (!results.length && coords.lat && coords.lng) {
      setNearbyRegions([]);
      const SecurityID = (window as any).securityID ?? "";
      const url = `${window.location.pathname}/nearby?SecurityID=${SecurityID}&lat=${coords.lat}&lng=${coords.lng}`;

      try {
        fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        })
          .then((response) => response.json())
          .then((data) => {
            if (data) {
              setNearbyRegions(data.results);
            } else {
              setNearbyRegions([]);
            }
          });
      } catch (error) {
        console.error(error);
      }
    } else {
      setNearbyRegions([]);
    }
  };

  /**
   * Handles the selection of a location.
   * @param {Option} value - the selected option
   */
  const handleSelectLocation = async (value: Option) => {
    if (!value) {
      setSelectedPlace(null);
      setShowLocationError(false);
      setIsUserLocation(false);
      setShowResults(false);
      return;
    }

    try {
      const latLng = await geocodeByPlaceId(value.value.place_id);

      if (latLng && latLng.length > 0) {
        const place = {
          label: value.label,
          value: {
            ...value.value,
            geometry: latLng[0].geometry,
          },
        };

        setSelectedPlace(place);
        setShowLocationError(false);
        updateFilters(selectedFilters);

        if (showResults) {
          setLoading(true);
          updateProviders(selectedFilters, place);
        }
      }
    } catch (error) {
      console.error(error);
    }

    setIsUserLocation(false);
  };

  /**
   * Handle changing any of the filters and radio buttons
   *
   * @param e
   */
  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // if value  is all then remove the filter
    const updatedFilters = selectedFilters.filter(
      (f) => f.name !== e.target.name
    );

    if (e.target.value !== "all") {
      updatedFilters.push({
        name: e.target.name,
        value: e.target.value,
      });
    }

    updateFilters(updatedFilters);
    updateProviders(updatedFilters, selectedPlace);
  };

  /**
   * Get user's device location.
   */
  const handleCurrentLocation = () => {
    setLocationLoading(true);

    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(function (position) {
        geocodeByLatLng({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        })
          .then((results) => {
            const place = {
              label: results[0].formatted_address,
              value: results[0],
            };

            setSelectedPlace(place);
            updateProviders(selectedFilters, place);
          })
          .catch((error) => console.error(error));

        setIsUserLocation(true);
        setLocationLoading(false);
      });
    } else {
      // Browser doesn't support Geolocation
      console.log("Geolocation is not available in your browser.");
      setIsUserLocation(false);
      setLocationLoading(false);
    }
  };

  /**
   * Generate the location button based on the state of the form.
   * @return {JSX.Element}
   */
  const userLocationButton = useMemo(() => {
    if (isUserLocation) {
      return (
        <p className="provider__form__caption">Using your current location</p>
      );
    } else {
      if (locationLoading) {
        return <p className="provider__form__caption">Loading...</p>;
      }
      return (
        <button
          type="button"
          className="provider__form__geolocation"
          onClick={handleCurrentLocation}
        >
          Use my current location
        </button>
      );
    }
  }, [isUserLocation, locationLoading]);

  // Select props
  const autocompleteProps: GooglePlacesAutocompleteProps["selectProps"] = {
    value: selectedPlace,
    placeholder: "Type suburb, town or region",
    noOptionsMessage: () => "Type suburb, town or region",
    onChange: handleSelectLocation,
    inputId: "field-location",
    className: "provider__form__input",
    styles: autocompleteStyles,
    theme: autocompleteTheme,
    openMenuOnFocus: false,
    isClearable: true,
    components: {},
  };

  return (
    <form
      ref={formRef}
      onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!selectedPlace && locationFilter && locationFilter.requiredSearchFilter) {
          setShowLocationError(true);
          setShowResults(false);
          return false;
        }

        if (selectedPlace) {
          setShowLocationError(false);
        }

        setShowResults(true);
        updateProviders(selectedFilters, selectedPlace);
      }}
      className="no-print"
    >
      <div
        className={`provider__form ${
          radioFilters ? "provider__form--with-radio" : ""
        } ${locationFilter ? "provider__form--with-location" : ""}`}
      >
        {locationFilter && (
          <div className="provider__form__col provider__form__col--large">
            <fieldset>
              <label className="provider__form__label" htmlFor="field-location">
                {locationFilter.label}
              </label>

              {isApiLoaded && renderAutocomplete && (
                <GooglePlacesAutocomplete
                  ref={selectInputRef}
                  apiOptions={{ language: "en", region: "nz" }}
                  autocompletionRequest={autocompleteOptions}
                  selectProps={autocompleteProps}
                  onLoadFailed={(error) => {
                    console.error(error);
                  }}
                />
              )}
            </fieldset>

            {showLocationError && (
              <span className="provider__form__error">
                Please select your location
              </span>
            )}
            <div>{userLocationButton}</div>
          </div>
        )}
        {radioFilters &&
          radioFilters.map((radioFilter) => (
            <div
              className="provider__form__col provider__form__col--small"
              key={radioFilter.label}
            >
              <fieldset>
                <legend className="provider__form__label">
                  {radioFilter.label}
                </legend>

                {radioFilter.options &&
                  radioFilter.options.map((option) => (
                    <div key={option} className="provider__form__radio">
                      <input
                        type="radio"
                        id={option}
                        name={radioFilter.name}
                        value={option}
                        required={radioFilter.requiredSearchFilter}
                        onChange={handleFilterChange}
                      />
                      <label htmlFor={option}>{option}</label>
                    </div>
                  ))}
              </fieldset>
            </div>
          ))}

        <div className="provider__form__actions">
          <button type="submit" className="provider__form__btn">
            {configuration.buttonLabel.length > 0
              ? configuration.buttonLabel
              : "Find a provider"}
          </button>
          <button
            type="reset"
            className="provider__form__btn"
            onClick={resetForm}
          >
            Reset
          </button>
        </div>
      </div>

      {showResults && modalFilters && modalFilters.length > 0 && (
        <>
          <div className="provider__filters no-print">
            <button
              type="button"
              className="button provider__button"
              onClick={() => {
                setShowModal(true);
              }}
            >
              Filters{" "}
              {filterCount > 0 ? (
                <span className="provider__button-count">{filterCount}</span>
              ) : null}
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0 11.125C0 10.7695 0.273438 10.4688 0.65625 10.4688H2.26953C2.54297 9.59375 3.39062 8.9375 4.375 8.9375C5.33203 8.9375 6.17969 9.59375 6.45312 10.4688H13.3438C13.6992 10.4688 14 10.7695 14 11.125C14 11.5078 13.6992 11.7812 13.3438 11.7812H6.45312C6.17969 12.6836 5.33203 13.3125 4.375 13.3125C3.39062 13.3125 2.54297 12.6836 2.26953 11.7812H0.65625C0.273438 11.7812 0 11.5078 0 11.125ZM5.25 11.125C5.25 10.6602 4.83984 10.25 4.375 10.25C3.88281 10.25 3.5 10.6602 3.5 11.125C3.5 11.6172 3.88281 12 4.375 12C4.83984 12 5.25 11.6172 5.25 11.125ZM9.625 4.5625C10.582 4.5625 11.4297 5.21875 11.7031 6.09375H13.3438C13.6992 6.09375 14 6.39453 14 6.75C14 7.13281 13.6992 7.40625 13.3438 7.40625H11.7031C11.4297 8.30859 10.582 8.9375 9.625 8.9375C8.64062 8.9375 7.79297 8.30859 7.51953 7.40625H0.65625C0.273438 7.40625 0 7.13281 0 6.75C0 6.39453 0.273438 6.09375 0.65625 6.09375H7.51953C7.79297 5.21875 8.64062 4.5625 9.625 4.5625ZM10.5 6.75C10.5 6.28516 10.0898 5.875 9.625 5.875C9.13281 5.875 8.75 6.28516 8.75 6.75C8.75 7.24219 9.13281 7.625 9.625 7.625C10.0898 7.625 10.5 7.24219 10.5 6.75ZM13.3438 1.71875C13.6992 1.71875 14 2.01953 14 2.375C14 2.75781 13.6992 3.03125 13.3438 3.03125H7.32812C7.05469 3.93359 6.20703 4.5625 5.25 4.5625C4.26562 4.5625 3.41797 3.93359 3.14453 3.03125H0.65625C0.273438 3.03125 0 2.75781 0 2.375C0 2.01953 0.273438 1.71875 0.65625 1.71875H3.14453C3.41797 0.84375 4.26562 0.1875 5.25 0.1875C6.20703 0.1875 7.05469 0.84375 7.32812 1.71875H13.3438ZM4.375 2.375C4.375 2.86719 4.75781 3.25 5.25 3.25C5.71484 3.25 6.125 2.86719 6.125 2.375C6.125 1.91016 5.71484 1.5 5.25 1.5C4.75781 1.5 4.375 1.91016 4.375 2.375Z"
                  fill="#2F3A49"
                />
              </svg>
            </button>
            {activeModalFilters.length > 0 &&
              activeModalFilters.map((activeFilter) => (
                <button
                  className="provider__filters-pill"
                  key={activeFilter.name}
                  onClick={() => {
                    updateFilters(
                      selectedFilters.filter(
                        (filter) => filter.name !== activeFilter.name
                      )
                    );
                  }}
                >
                  {activeFilter.value}

                  <MemoCloseIcon />
                </button>
              ))}
          </div>
          <SearchProviderFilterModal
            filters={modalFilters}
            isOpen={showModal}
            totalProviders={providerContext.totalMatchingProviders}
            selectedFilters={selectedFilters}
            handleFilterChange={handleFilterChange}
            closeModal={() => {
              setShowModal(false);
            }}
          />
        </>
      )}
    </form>
  );
};

export default SearchProviderForm;
